import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { dashboardService } from '@/services/dashboardService';
import type { TransactionFilters, Transaction } from '@/types/transaction';
import type { ErrorFilters, ProcessingError } from '@/types/error';
import type { ApiError } from '@/services/api';

// ============ User Dashboard Hooks ============

export function useDashboardSummary(period: 'week' | 'month' | 'year' = 'month') {
  return useQuery({
    queryKey: ['dashboard', 'summary', period],
    queryFn: () => dashboardService.getSummary(period),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

export function useDashboardTrends(period: 'week' | 'month' | 'year' = 'month') {
  return useQuery({
    queryKey: ['dashboard', 'trends', period],
    queryFn: () => dashboardService.getTrends(period),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

export function useRecentTransactions(limit: number = 10) {
  return useQuery({
    queryKey: ['dashboard', 'recent', limit],
    queryFn: () => dashboardService.getRecentTransactions(limit),
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

export function useReceiptStats() {
  return useQuery({
    queryKey: ['dashboard', 'receipts', 'stats'],
    queryFn: () => dashboardService.getReceiptStats(),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// ============ Transactions Hooks ============

export function useTransactions(filters: TransactionFilters = {}) {
  return useQuery({
    queryKey: ['transactions', filters],
    queryFn: () => dashboardService.getTransactions(filters),
    staleTime: 1000 * 60 * 2, // 2 minutes
  });
}

export function useTransaction(id: string) {
  return useQuery({
    queryKey: ['transactions', id],
    queryFn: () => dashboardService.getTransaction(id),
    enabled: !!id,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

export function useUpdateTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Transaction> }) =>
      dashboardService.updateTransaction(id, data),
    onSuccess: (updatedTransaction) => {
      // Update the individual transaction cache
      queryClient.setQueryData(['transactions', updatedTransaction.id], updatedTransaction);
      // Invalidate the transactions list to refetch
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      // Invalidate dashboard data
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: (error: ApiError) => {
      console.error('Transaction update failed:', error.message);
    },
  });
}

export function useDeleteTransaction() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => dashboardService.deleteTransaction(id),
    onSuccess: (_, deletedId) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: ['transactions', deletedId] });
      // Invalidate the transactions list
      queryClient.invalidateQueries({ queryKey: ['transactions'] });
      // Invalidate dashboard data
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: (error: ApiError) => {
      console.error('Transaction deletion failed:', error.message);
    },
  });
}

// ============ Admin Dashboard Hooks ============

export function useAdminStats() {
  return useQuery({
    queryKey: ['admin', 'stats'],
    queryFn: () => dashboardService.getAdminStats(),
    staleTime: 1000 * 60 * 1, // 1 minute
    refetchInterval: 1000 * 60 * 5, // Refetch every 5 minutes
  });
}

export function useErrors(filters: ErrorFilters = {}) {
  return useQuery({
    queryKey: ['admin', 'errors', filters],
    queryFn: () => dashboardService.getErrors(filters),
    staleTime: 1000 * 30, // 30 seconds
  });
}

export function useErrorStats() {
  return useQuery({
    queryKey: ['admin', 'errors', 'stats'],
    queryFn: () => dashboardService.getErrorStats(),
    staleTime: 1000 * 60 * 1, // 1 minute
  });
}

export function useError(id: string) {
  return useQuery({
    queryKey: ['admin', 'errors', id],
    queryFn: () => dashboardService.getError(id),
    enabled: !!id,
    staleTime: 1000 * 60 * 1, // 1 minute
  });
}

export function useUpdateError() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<ProcessingError> }) =>
      dashboardService.updateError(id, data),
    onSuccess: (updatedError) => {
      // Update the individual error cache
      queryClient.setQueryData(['admin', 'errors', updatedError.id], updatedError);
      // Invalidate the errors list
      queryClient.invalidateQueries({ queryKey: ['admin', 'errors'] });
    },
    onError: (error: ApiError) => {
      console.error('Error update failed:', error.message);
    },
  });
}

export function useRetryProcessing() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (errorId: string) => dashboardService.retryProcessing(errorId),
    onSuccess: () => {
      // Invalidate errors list to refetch
      queryClient.invalidateQueries({ queryKey: ['admin', 'errors'] });
      queryClient.invalidateQueries({ queryKey: ['admin', 'stats'] });
    },
    onError: (error: ApiError) => {
      console.error('Retry processing failed:', error.message);
    },
  });
}

export default {
  useDashboardSummary,
  useDashboardTrends,
  useRecentTransactions,
  useReceiptStats,
  useTransactions,
  useTransaction,
  useUpdateTransaction,
  useDeleteTransaction,
  useAdminStats,
  useErrors,
  useErrorStats,
  useError,
  useUpdateError,
  useRetryProcessing,
};
